import type { Document as RichTextDocument } from '@contentful/rich-text-types';
import Grid from '@mui/material/Grid';
import { Fragment, type ReactNode } from 'react';

import Checkbox from '@/components/Checbox/Checkbox';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import RichText from '@/components/RichText/RichText';
import {
  SpacingContainer,
  type SpacingContainerProps,
} from '@/components/SpacingContainer/SpacingContainer';
import useGlobalStyles from '@/styles/global.styles';

import { DescriptionPlacement } from './descriptionPlacement';
import useStyles from './ProductPageBase.styles';

interface ProductPageBaseProps {
  isBattery?: boolean;
  isOnDark?: boolean;
  heading: string;
  subheading: string;
  imageContent: ReactNode;
  description?: string;
  descriptionHtml?: string;
  descriptionPlacement?: DescriptionPlacement;
  optionCardLists: ReactNode[];
  totalPrice?: string;
  checkoutButtons: ReactNode[];
  disclaimer?: string;
  requireDisclaimerCheckbox?: boolean;
  disclaimerCheckboxTerms?: RichTextDocument;
  isDisclaimerAcknowledged?: boolean;
  onDisclaimerAcknowledgedChange?: (acknowledged: boolean) => void;
  className?: string;
}

const ProductPageBase: React.FC<ProductPageBaseProps> = ({
  isBattery = false,
  isOnDark = false,
  heading,
  subheading,
  imageContent,
  description,
  descriptionHtml,
  optionCardLists,
  totalPrice,
  checkoutButtons,
  disclaimer,
  requireDisclaimerCheckbox = false,
  disclaimerCheckboxTerms,
  isDisclaimerAcknowledged = false,
  onDisclaimerAcknowledgedChange,
  className,
  descriptionPlacement = DescriptionPlacement.ABOVE_PRICE_AND_PURCHASE_OPTIONS,
}) => {
  const { classes, cx } = useStyles({ isOnDark });
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });

  const descriptionContent = (
    <Fragment>
      {description && <p className={globalClasses.body}>{description}</p>}
      {descriptionHtml && (
        <div
          className={globalClasses.body}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: Shopify is a trusted source
          dangerouslySetInnerHTML={{ __html: descriptionHtml }}
        />
      )}
    </Fragment>
  );

  const spacingContainerProps: SpacingContainerProps = isBattery
    ? { spacingLevelOverride: 4 }
    : { isFeatured: true };

  // Contentful may set requireDisclaimerCheckbox with an empty disclaimer symbol;
  // the acknowledgment checkbox (and optional disclaimerCheckboxTerms) still render.
  const disclaimerElement =
    requireDisclaimerCheckbox || disclaimer ? (
      <div className={classes.disclaimerSection}>
        {requireDisclaimerCheckbox ? (
          <div className={classes.disclaimerLabel}>
            <span className={classes.disclaimerCheckbox}>
              <Checkbox
                checked={isDisclaimerAcknowledged}
                onChange={(event) =>
                  onDisclaimerAcknowledgedChange?.(event.target.checked)
                }
                inputProps={{ 'aria-label': 'Disclaimer acknowledgment' }}
                isOnDark={isOnDark}
              />
            </span>
            {disclaimer && <p className={classes.disclaimer}>{disclaimer}</p>}
          </div>
        ) : (
          disclaimer && <p className={classes.disclaimer}>{disclaimer}</p>
        )}
        {requireDisclaimerCheckbox && disclaimerCheckboxTerms && (
          <RichText
            content={disclaimerCheckboxTerms}
            className={classes.disclaimerCheckboxTerms}
            isOnDark={isOnDark}
          />
        )}
      </div>
    ) : null;

  return (
    <SpacingContainer
      {...spacingContainerProps}
      className={cx(classes.container, className)}
    >
      <GridContainer fixed>
        <Grid size={12}>
          <h2 className={cx(globalClasses.h2, classes.heading)}>{heading}</h2>
          <h3 className={cx(globalClasses.h4, classes.subheading)}>
            {subheading}
          </h3>
        </Grid>
        <Grid className={classes.body} size={12}>
          <div className={classes.carouselContainer}>{imageContent}</div>
          <div className={classes.productInformationContainer}>
            {descriptionPlacement ===
              DescriptionPlacement.ABOVE_PRICE_AND_PURCHASE_OPTIONS &&
              descriptionContent}
            {optionCardLists}
            {totalPrice && <p className={globalClasses.h3}>{totalPrice}</p>}
            {descriptionPlacement ===
              DescriptionPlacement.BELOW_PRICE_AND_PURCHASE_OPTIONS &&
              descriptionContent}
            <div className={classes.checkoutButtons}>{checkoutButtons}</div>
            {disclaimerElement}
          </div>
        </Grid>
      </GridContainer>
    </SpacingContainer>
  );
};

export default ProductPageBase;
